import { sentryCaptureException } from "../sentry/library";
import { isServer } from "../environmentDetection/library";

function getLogLevel() {
    if (process.env.NODE_ENV === "debug" || process.env.LOG_LEVEL === "debug") {
        return "debug";
    }
    return "info";
}

const logLevel = getLogLevel();

/**
 * Client side logger
 * Logs message and error (if applicable) to the console at the appropriate level.
 * If console.error(message, error) is called with a second argument that is an instance of the Error class, the error will be sent to Sentry.
 *
 * @returns {object} logger object with debug, info, warn, and error methods
 */
function createBrowserLogger() {
    const LOGGER_PREFIX = "logger:";

    // Formats message and error console output for logger.warn and logger.error
    // Format: "logger: console message  [Error: error message]"
    const formatForConsole = (message, error) => {
        if (!error) {
            return `${LOGGER_PREFIX} ${message}`;
        }

        error = error instanceof Array ? error[0] : error;

        let errorName;
        if (error instanceof Error) {
            errorName = error.name ? error.name : "Error";
        } else {
            errorName = "exception";
        }

        const errorMessage = error.message ? error.message : error;
        const formattedError = errorMessage
            ? `[${errorName}: ${errorMessage}]`
            : `[${errorName}]`;
        return `${LOGGER_PREFIX} ${message}  ${formattedError}`;
    };

    return {
        // All arguments for console.debug and console.info will be logged out to the console.
        debug: (...args) => {
            if (logLevel === "debug") {
                console.debug(LOGGER_PREFIX, ...args);
            }
        },
        info: (...args) => {
            console.info(LOGGER_PREFIX, ...args);
        },
        // For console.warn and console.error, the arguments are explicitly defined so that we can format the error message.
        // @param {string} message - Message that is logged to the console
        // @param {object} error - Error that is logged to the console.
        warn: (message, error) => {
            const errorInConsole = formatForConsole(message, error);
            console.warn(errorInConsole);
        },
        // For console.error, the second error argument will be sent to Sentry for reporting ONLY IF it is an instance of the Error class.
        error: (message, error) => {
            const errorInConsole = formatForConsole(message, error);
            console.error(errorInConsole);
            if (error && error instanceof Error) {
                sentryCaptureException(error);
            }
        },
    };
}

function createServerLogger() {
    const { createLogger, format, transports } = require("winston");

    const logger = createLogger({
        level: logLevel,
        format: format.combine(
            format.timestamp({ format: "YYYY-MM-DD HH:mm:ss" }),
            format.errors({ stack: true }),
            format.splat(),
            format.json()
        ),
    });

    // If we're in production, use just the simple logger, otherwise use the colorized logger
    logger.add(
        new transports.Console({
            format:
                process.env.NODE_ENV === "production"
                    ? format.simple()
                    : format.combine(format.colorize(), format.simple()),
        })
    );

    return logger;
}

/**
 * @typedef {Object} Logger
 *
 * @property {(...args) => void} debug - Logs all args as console.debug() message if logLevel === "debug".
 * @property {(...args) => void} info - Logs all args as console.info() message if logLevel === "info".
 * @property {(message: string, error?: Error) => void} warn - Logs a console.warn() message if logLevel === "warn".
 *      `message` - Warning message logged to the console
 *      `error` - Optional Error object whose message will be logged to the console.
 * @property {(message: string, error?: Error) => void} error - Logs a console.error() message if logLevel === "error" and sends Error to Sentry.
 *      `message` - Error message logged to the console.
 *      `error` - Optional Error object whose message will be logged to the console. If a valid Error class is passed, the error will be sent to Sentry (client side only).
 */

/**
 * Client/Server Logger
 * Logs message and error (if applicable) to the console at the appropriate level.
 *
 * @type {Logger} Logger object with debug, info, warn, and error methods
 */
const logger = isServer() ? createServerLogger() : createBrowserLogger();

export default logger;
